import { PrimaryButton } from "@/components/buttons/PrimaryButton";
import PencomCheckBox from "@/components/input/PencomCheckBox";
import PencomFileUpload from "@/components/input/PencomFileUpload";
import PencomSegmentedDatePicker from "@/components/input/PencomSegmentedDatePicker";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm } from "@tanstack/react-form";
import { type VerifyCompanyDetailsForm } from "@/forms/auth/auth.form";
import { PencomRoutes } from "@/router";
import { toast } from "@/utils/toast";
import { useRegisterEmployer } from "@/hooks/auth/auth.hook";
import { useFileUpload } from "@/hooks/file-upload/file-upload.hook";
import { EStorage } from "@/constants/enums/storage.enum";
import { useEffect, useState } from "react";
import type { AxiosError } from "axios";
import { maskEmail, maskPhone } from "@/utils/maskUtils";
import { useAnalytics } from "@/hooks/useAnalytics";
import { MissingContactEmailDialog } from "@/components/onboarding/MissingContactEmailDialog";

const VerifyCompanyDetails = () => {
  const data = useLocation().state as {
    data: { tempData: Record<string, string | undefined> };
  };
  const { mutate: registerEmployer, isPending } = useRegisterEmployer();
  const { mutate: uploadFile, isPending: isUploadingFile } = useFileUpload();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentUrl, setDocumentUrl] = useState<string>("");
  const [isMissingEmailDialogOpen, setIsMissingEmailDialogOpen] =
    useState<boolean>(false);
  const navigate = useNavigate();
  const { trackEvent, eventNames } = useAnalytics();
  const tempData = data?.data?.tempData ?? {};

  const form = useForm({
    defaultValues: {
      verifyCompanyDetails: false,
      incorporationDate: tempData.incorporationDate || "",
    } as VerifyCompanyDetailsForm,
  });

  const handleRegisterEmployer = () => {
    const trimmedContactEmail = tempData.contactEmail?.trim() ?? "";
    if (!trimmedContactEmail) {
      setIsMissingEmailDialogOpen(true);
      return;
    }

    registerEmployer(
      {
        rcNumber: tempData.rcNumber ?? "",
        employerCode: tempData.employerCode ?? "",
        companyName: tempData.companyName ?? "",
        employerType: "Limited",
        sector: "0",
        natureOfBusiness: tempData.natureOfBusiness ?? "",
        firstName: "empty",
        lastName: "empty",
        contactEmail: trimmedContactEmail,
        contactPhoneNumber: tempData.contactPhoneNumber?.trim() ?? "",
        registeredAddress: tempData.registeredAddress?.trim() ?? "",
        companyType: "empty",
        incorporationDate: form.state.values.incorporationDate,
        cacCertificateUrl: documentUrl,
      },
      {
        onSuccess: () => {
          // toast.success("Employer registered successfully");
          trackEvent(eventNames.onboardingStepSubmitted, {
            step: "verify_company_details",
            status: "success",
          });
          navigate(PencomRoutes.EMPLOYER_REG_OTP_VERIFICATION, {
            state: data?.data?.tempData,
          });
        },
        onError: (error) => {
          toast.error(error.message);
          trackEvent(eventNames.onboardingStepSubmitted, {
            step: "verify_company_details",
            status: "error",
            message: error.message,
          });
        },
      }
    );
  };

  useEffect(() => {
    trackEvent(eventNames.onboardingPageView, {
      step: "verify_company_details",
    });
  }, []);

  const handleReportIssueNavigation = () => {
    navigate(PencomRoutes.REPORT_DISCREPENCY, { state: data });
  };

  const handleFileSelect = (file: File | null) => {
    if (!file) {
      setSelectedFile(null);
      setDocumentUrl("");
      return;
    }
    setSelectedFile(file);
    uploadFile(
      {
        file,
        path: EStorage.CAC_DOCUMENT,
        document_type: EStorage.CAC_DOCUMENT,
      },
      {
        onSuccess: (
          response: { data?: { url?: string }; url?: string } | string
        ) => {
          toast.success("File uploaded successfully");
          const url =
            typeof response === "string"
              ? response
              : response?.url ?? response?.data?.url ?? "";
          setDocumentUrl(url);
        },
        onError: (error) => {
          const err = error as AxiosError<{ message?: string }>;
          toast.error(err.response?.data?.message ?? "Failed to upload file");
          setSelectedFile(null);
          setDocumentUrl("");
        },
      }
    );
  };

  const handleFileRemove = () => {
    setSelectedFile(null);
    setDocumentUrl("");
  };

  return (
    <div className="w-full flex flex-col">
      <h2 className="font-clash-display font-semibold text-3xl md:text-[40px] text-medium-dark-grey leading-[1.25] tracking-[-0.6px]">
        Verify company details
      </h2>
      <p className="font-clash-display font-medium text-base text-medium-dark-grey leading-6 tracking-[-0.08px] mt-3">
        We retrieved the following company details from PenCom records. Please
        confirm that this information is correct to continue.
      </p>

      <div className="bg-white border border-stroke rounded-lg overflow-hidden mt-8">
        <div className="flex gap-4 items-center border-b border-stroke px-4 py-3">
          <div className="bg-pale-mint-green rounded-full size-8 flex items-center justify-center shrink-0">
            <img
              src="/assets/icons/building.svg"
              alt=""
              className="size-6"
              loading="lazy"
              aria-hidden
            />
          </div>
          <p className="font-clash-display font-semibold text-[20px] text-medium-dark-grey">
            {tempData.companyName ?? ""}
          </p>
        </div>
        <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6">
          <div className="flex flex-col gap-1">
            <p className="font-clash-display font-normal text-sm text-medium-dark-grey">
              RC Number
            </p>
            <p className="font-clash-display font-semibold text-sm leading-6 text-medium-dark-grey">
              {tempData.rcNumber ?? ""}
            </p>
          </div>
          <div className="flex flex-col gap-1">
            <p className="font-clash-display font-normal text-sm text-medium-dark-grey">
              Employer Code
            </p>
            <p className="font-clash-display font-semibold text-sm leading-6 text-medium-dark-grey">
              {tempData.employerCode || ""}
            </p>
          </div>
          <div className="flex flex-col gap-1">
            <p className="font-clash-display font-normal text-sm text-medium-dark-grey">
              Company Type
            </p>
            <p className="font-clash-display font-semibold text-sm leading-6 text-medium-dark-grey">
              {tempData?.companyType || "N/A"}
            </p>
          </div>
          <div className="flex flex-col gap-1">
            <p className="font-clash-display font-normal text-sm text-medium-dark-grey">
              Registered Address
            </p>
            <p className="font-clash-display font-semibold text-sm leading-6 text-medium-dark-grey">
              {tempData.registeredAddress ?? ""}
            </p>
          </div>
          <div className="flex flex-col gap-1">
            <p className="font-clash-display font-normal text-sm text-medium-dark-grey">
              Contact Email
            </p>
            <p className="font-clash-display font-semibold text-sm leading-6 text-medium-dark-grey">
              {maskEmail(tempData.contactEmail ?? "")}
            </p>
          </div>
          <div className="flex flex-col gap-1">
            <p className="font-clash-display font-normal text-sm text-medium-dark-grey">
              Contact Phone Number
            </p>
            <p className="font-clash-display font-semibold text-sm leading-6 text-medium-dark-grey">
              {maskPhone(tempData.contactPhoneNumber ?? "")}
            </p>
          </div>
        </div>
      </div>

      <p className="font-clash-display font-medium text-base text-medium-dark-grey leading-[25px] tracking-[-0.08px] mt-6">
        If any details are incorrect,{" "}
        <button
          type="button"
          className="text-primary-button underline cursor-pointer hover:opacity-90"
          onClick={handleReportIssueNavigation}
        >
          report the issue
        </button>{" "}
        before continuing.
      </p>

      <p className="font-clash-display font-semibold text-md text-medium-dark-grey tracking-[-0.08px] mt-10 mb-5">
        Additional Company Information
      </p>

      <form.Field
        name="incorporationDate"
        children={(field) => (
          <div className="mt-2">
            <PencomSegmentedDatePicker
              id="incorporation-date"
              label="Incorporation Date"
              labelClassName="text-md font-medium"
              descriptionText="Enter the date shown on your Certificate of Incorporation."
              value={field.state.value || ""}
              onChange={(value) => field.handleChange(value)}
              max={new Date().toISOString().split("T")[0]}
              error={field.state.meta.errors.length > 0}
            />
            {field.state.meta.errors.length > 0 && (
              <p className="text-red-500 text-md mt-1">
                {field.state.meta.errors[0]}
              </p>
            )}
          </div>
        )}
      />

      <p className="font-clash-display font-medium text-md text-medium-dark-grey tracking-[-0.08px] mt-6">
        Certificate of Incorporation (CAC)
      </p>
      <p className="font-clash-display font-medium text-xs text-medium-grey tracking-[-0.06px] mt-1">
        Upload a clear copy of your Certificate of Incorporation.
      </p>
      <div className="mt-2">
        <PencomFileUpload
          id="cac-certificate-upload"
          label=""
          placeholder="Click here or drag and drop to upload"
          accept=".pdf,.jpg,.jpeg,.png"
          maxSize={10}
          onFileSelect={handleFileSelect}
          selectedFile={selectedFile}
          disabled={isUploadingFile}
          onFileRemove={handleFileRemove}
        />
      </div>

      <form.Field
        name="verifyCompanyDetails"
        children={(field) => (
          <PencomCheckBox
            labelClassName="mt-8"
            id="verify-company-details"
            name="verify-company-details"
            value="verify-company-details"
            label="I confirm these details are accurate and belong to my organisation."
            isChecked={field.state.value}
            onToggle={(checked) => field.handleChange(checked)}
          />
        )}
      />

      <form.Subscribe
        selector={(state) => ({
          verifyCompanyDetails: state.values.verifyCompanyDetails,
          incorporationDate: state.values.incorporationDate,
        })}
        children={({ verifyCompanyDetails, incorporationDate }) => (
          <PrimaryButton
            className="mt-10 w-full"
            title="Confirm & Continue"
            isLoading={isPending}
            onClick={handleRegisterEmployer}
            disabled={
              !verifyCompanyDetails || !incorporationDate || !documentUrl
            }
          />
        )}
      />

      <MissingContactEmailDialog
        open={isMissingEmailDialogOpen}
        onOpenChange={setIsMissingEmailDialogOpen}
        onReportIssue={() => {
          setIsMissingEmailDialogOpen(false);
          handleReportIssueNavigation();
        }}
      />
    </div>
  );
};

export default VerifyCompanyDetails;
